import { sentenceCase } from 'change-case';
import { useCallback, useEffect, useState } from 'react';
// material
import {
  Card,
  Checkbox,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Typography,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/system';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
//
import Page from '../../components/Page';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import LogInToolbar from './LogInToolbar';
import useLoginModuleSelector from '../../store/Slice/LoginModule/LoginModuleSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getLoginGridData } from '../../store/Slice/LoginModule/LoginModuleSlice';
import useDebounce from '../../utils/useDebounce';

// ----------------------------------------------------------------------

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: '#fff',
  '&:nth-of-type(even)': {
    backgroundColor: '#fafbfc'
  },
  '&:hover': {
    backgroundColor: '#f1f5f9'
  },
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    padding: '12px 14px',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: '#64748b',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155'
  }
}));

const TABLE_HEAD = [
  { id: 'id', label: 'APPLICATION ID', alignRight: false },
  { id: 'created', label: 'SUBMISSION DATE', alignRight: false },
  { id: 'name', label: 'NAME', alignRight: false },
  { id: 'loanType', label: 'PRODUCT', alignRight: false },
  { id: 'city', label: 'CITY', alignRight: false },
  { id: 'amount', label: 'AMOUNT', alignRight: false },
  { id: 'bankName', label: 'BANK', alignRight: false },
  { id: 'status', label: 'STATUS', alignRight: false },
  { id: 'created', label: 'LAST UPDATE', alignRight: false },
  { id: 'action', label: 'ACTION', alignRight: false, align: 'center' }
];

// ----------------------------------------------------------------------

/**
 * Represents a book.
 * @constructor
 * @param {string} title - The title of the book.
 * @param {string} author - The author of the book.
 */
export default function LogIn() {
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [filterState, setFilterState] = useState({
    product: ' ',
    productId: null,
    productArray: [],
    city: null,
    cityId: null,
    cityArray: [],
    status: ' ',
    statusId: null,
    statusArray: [],
    employmentType: ' ',
    employmentTypeId: null,
    employmentTypeArray: []
  });
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const [hasNextPage, setHasNextPage] = useState(true);

  const dispatch = useDispatch();

  const { LoginData } = useLoginSelector();
  const { LoginGridData } = useLoginModuleSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const getLoginGridApi = useCallback(() => {
    const objPayload = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      stageId: '58',
      log_inFilterModel: {
        cityId: filterState.cityId || null,
        fromLeadDate:
          filterDatesRange[0].startDate !== null ? fDateTime2(filterDatesRange[0].startDate) : null,
        toLeadDate:
          filterDatesRange[0].endDate !== null ? fDateTime2(filterDatesRange[0].endDate) : null,
        leadStatusId: filterState.status || null,
        loanTypeId: filterState.product || null
      },
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        sortOrder: order || null,
        sortLiteral: order || null,
        pageSize: rowsPerPage,
        pageNumber: page + 1
      }
    };
    dispatch(getLoginGridData(objPayload));
  }, [
    LoginData,
    debouncedSearch,
    filterDatesRange,
    filterState,
    order,
    page,
    rowsPerPage,
    dispatch
  ]);

  useEffect(() => {
    getLoginGridApi();
  }, [getLoginGridApi]);

  useEffect(() => {
    const leadDetails = localStorage.getItem('leadDetails');
    if (leadDetails !== null) {
      localStorage.removeItem('leadDetails');
    }
  }, []);

  const handleRequestSort = (event, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = LoginGridData?.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  useEffect(() => {
    if (!LoginGridData) {
      setHasNextPage(true);
      return;
    }

    // If API returned fewer rows → last page
    setHasNextPage(LoginGridData.length >= rowsPerPage);
  }, [LoginGridData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return (
    <Page title="Kpros by Kalolytic Solutions || Log In">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title, and Breadcrumb */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Log In
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Track and manage customer loan applications and partner logins.
              </Typography>
            </Box>
          </Stack>

          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <LogInToolbar
            numSelected={selected.length}
            filterName={filterName}
            setFilterName={setFilterName}
            onFilterName={(e) => setFilterName(e.target.value)}
            filterDatesRange={filterDatesRange}
            setFilterDatesRange={setFilterDatesRange}
            filterState={filterState}
            setFilterState={setFilterState}
            selected={selected}
            parent="login"
          />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, minHeight: 380, overflow: 'auto' }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={LoginGridData?.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  isCheckBox="true"
                />
                <TableBody>
                  {LoginGridData?.map((item, index) => {
                    const isItemSelected = selected.indexOf(item) !== -1;
                    return (
                      <StyledTableRow
                        hover
                        key={item.applicationId || index}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                        parent="login"
                      >
                        <TableCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            checked={isItemSelected}
                            onChange={(event) => handleClick(event, item)}
                            sx={{
                              color: '#cbd5e1',
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                            }}
                          />
                        </TableCell>
                        <StyledTableCell align="left" sx={{ color: '#0284c7', fontWeight: 600 }}>
                          {item?.applicationId !== null && item?.applicationId !== ''
                            ? item?.applicationId
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {item?.submissionDate !== null && item?.submissionDate !== ''
                            ? fDate(item.submissionDate)
                            : NOT_AVAILABLE}
                        </StyledTableCell>

                        <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {item?.leadName !== null && item?.leadName !== ''
                            ? item?.leadName
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {item?.loanTypeName !== null && item?.loanTypeName !== ''
                            ? item?.loanTypeName
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {item?.cityName !== null && item?.cityName !== ''
                            ? item?.cityName
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#0f172a', fontWeight: 600 }}>
                          {item?.loanAmount !== null && item?.loanAmount !== ''
                            ? item?.loanAmount
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {item?.bankPrefName !== null && item?.bankPrefName !== ''
                            ? item?.bankPrefName
                            : NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell align="left">
                          <Label
                            variant="ghost"
                            color={
                              item?.logInStatusName === 'Closed' ||
                              item?.logInStatusName === 'Rejected'
                                ? 'warning'
                                : 'success'
                            }
                          >
                            {item?.logInStatusName !== null && item?.logInStatusName !== ''
                              ? sentenceCase(item?.logInStatusName)
                              : NOT_AVAILABLE}
                          </Label>
                        </StyledTableCell>
                        <StyledTableCell align="left" sx={{ color: '#64748b' }}>
                          {item?.lastUpdatedOn !== null && item?.lastUpdatedOn !== ''
                            ? fDate(item.lastUpdatedOn)
                            : NOT_AVAILABLE}
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          <UserMoreMenu rowId={item} parent="login" />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {LoginGridData?.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell align="center" colSpan={12} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Modern Pagination Footer */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, px: 3, borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', gap: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>Showing</Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                {LoginGridData?.length || 0} records
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                Page <span style={{ fontWeight: 600, color: '#0f172a' }}>{page + 1}</span>
              </Typography>

              <Stack direction="row" spacing={1}>
                <IconButton
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  disabled={!hasNextPage}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    p: 0.75,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    '&.Mui-disabled': { opacity: 0.4, bgcolor: '#f8fafc' }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Container>
    </Page>
  );
}
